import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { Check, Mail, Users, TrendingUp } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { usePublicWaitlist, useCreateSignup } from '@/hooks/use-waitlist-api'
import { SignupForm } from '@/types'
import { useRecaptcha, extractRateLimitError, isRecaptchaError } from '@/hooks/use-recaptcha'

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  name: z.string().optional(),
  company: z.string().optional(),
})

export function PublicWaitlistPage() {
  const { slug } = useParams<{ slug: string }>()
  const [isSignedUp, setIsSignedUp] = useState(false)
  const [signupPosition, setSignupPosition] = useState<number | null>(null)

  const {
    data: waitlist,
    isLoading: waitlistLoading,
    error: waitlistError,
  } = usePublicWaitlist(slug!)

  const createSignupMutation = useCreateSignup(slug!)
  const { executeRecaptcha, isEnabled: recaptchaEnabled } = useRecaptcha({ action: 'signup' })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  })

  if (waitlistLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center mobile-padding safe-area-top safe-area-bottom">
        <div className="max-w-md w-full">
          <div className="skeleton h-12 mb-4" />
          <div className="skeleton h-6 mb-8" />
          <div className="skeleton-button mb-4" />
          <div className="skeleton-button mb-4" />
          <div className="skeleton-button" />
        </div>
      </div>
    )
  }

  if (waitlistError || !waitlist) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 mobile-padding safe-area-top safe-area-bottom">
        <Card className="max-w-md w-full mobile-fade-in">
          <CardContent className="pt-6 text-center mobile-padding">
            <div className="mx-auto mb-4 h-12 w-12 text-muted-foreground">
              <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">Waitlist not found</h3>
            <p className="text-muted-foreground">
              The waitlist you're looking for doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const primaryColor = waitlist.config?.branding?.primary_color || '#3B82F6'

  const onSubmit = async (data: SignupForm) => {
    try {
      // Generate reCAPTCHA token if enabled
      let recaptcha_token: string | null = null
      if (recaptchaEnabled) {
        recaptcha_token = await executeRecaptcha()
      }

      const result = await createSignupMutation.mutateAsync({
        email: data.email,
        name: data.name,
        company: data.company,
        recaptcha_token,
      })

      setSignupPosition(result.position)
      setIsSignedUp(true)
      toast.success('Successfully joined the waitlist!')
    } catch (error: any) {
      // Handle rate limit errors
      const rateLimitError = extractRateLimitError(error)
      if (rateLimitError.isRateLimited) {
        toast.error(rateLimitError.message, { duration: 6000 })
        return
      }

      // Handle reCAPTCHA verification errors
      if (isRecaptchaError(error)) {
        toast.error('Security check failed. Please try again.', { duration: 5000 })
        return
      }

      toast.error(error.message || 'Failed to join waitlist')
    }
  }

  if (isSignedUp) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: primaryColor + '05' }}
      >
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-6">
            <div
              className="mx-auto mb-4 h-12 w-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: primaryColor + '20' }}
            >
              <Check
                className="h-6 w-6"
                style={{ color: primaryColor }}
              />
            </div>

            <h2 className="text-2xl font-bold mb-2">
              You're on the list! 🎉
            </h2>

            <p className="text-muted-foreground mb-6">
              Thanks for joining our waitlist. We'll keep you updated on our progress.
            </p>

            {signupPosition && (
              <div className="bg-muted/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-muted-foreground">Your position</p>
                <p className="text-3xl font-bold" style={{ color: primaryColor }}>
                  #{signupPosition}
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Share with friends and move up the list faster!
              </div>

              <Button
                variant="outline"
                size="default"
                onClick={() => {
                  const shareUrl = window.location.href
                  const shareText = `Join me on the ${waitlist.name} waitlist!`

                  if (navigator.share) {
                    navigator.share({
                      title: waitlist.name,
                      text: shareText,
                      url: shareUrl,
                    })
                  } else {
                    // Fallback - copy to clipboard
                    navigator.clipboard.writeText(`${shareText} ${shareUrl}`)
                    toast.success('Share link copied!')
                  }
                }}
                className="w-full mobile-button"
              >
                Share with Friends
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center mobile-padding safe-area-top safe-area-bottom mobile-scroll"
      style={{ backgroundColor: primaryColor + '05' }}
    >
      <div className="max-w-md w-full mobile-fade-in">
        <Card className="tap-highlight">
          <CardContent className="pt-6 mobile-padding">
            {/* Logo/Brand */}
            {waitlist.config?.branding?.logo_url && (
              <div className="text-center mb-6">
                <img
                  src={waitlist.config.branding.logo_url}
                  alt={waitlist.name}
                  className="mx-auto h-12 w-auto max-w-[200px] object-contain"
                  loading="eager"
                  decoding="async"
                />
              </div>
            )}

            {/* Content */}
            <div className="text-center mb-8">
              <h1
                className="text-2xl sm:text-3xl font-bold mb-4 leading-tight"
                style={{ color: primaryColor }}
              >
                {waitlist.config?.content?.headline || 'Join our waitlist'}
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                {waitlist.config?.content?.subtitle || 'Be the first to know when we launch'}
              </p>
            </div>

            {/* Features (if available) */}
            {waitlist.config?.content?.features && waitlist.config.content.features.length > 0 && (
              <div className="mb-8">
                <ul className="space-y-2">
                  {waitlist.config.content.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <Check
                        className="h-4 w-4 flex-shrink-0"
                        style={{ color: primaryColor }}
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Signup Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="signup-email" className="sr-only">
                  Email address
                </label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="Enter your email"
                  error={errors.email?.message}
                  autoComplete="email"
                  inputMode="email"
                  {...register('email')}
                />
              </div>

              <div>
                <label htmlFor="signup-name" className="sr-only">
                  Name (optional)
                </label>
                <Input
                  id="signup-name"
                  placeholder="Your name (optional)"
                  error={errors.name?.message}
                  autoComplete="name"
                  {...register('name')}
                />
              </div>

              <div>
                <label htmlFor="signup-company" className="sr-only">
                  Company (optional)
                </label>
                <Input
                  id="signup-company"
                  placeholder="Company (optional)"
                  error={errors.company?.message}
                  autoComplete="organization"
                  {...register('company')}
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full mobile-button"
                style={{ backgroundColor: primaryColor }}
                loading={createSignupMutation.isPending}
              >
                <Mail className="mr-2 h-5 w-5" />
                Join the Waitlist
              </Button>
            </form>

            {/* Social Proof */}
            {waitlist.stats && waitlist.stats.total_signups > 0 && (
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{waitlist.stats.total_signups} joined</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    <span>Growing fast</span>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 pt-4 border-t text-center">
              <p className="text-xs text-muted-foreground">
                Powered by{' '}
                <a
                  href="/"
                  className="hover:underline"
                  style={{ color: primaryColor }}
                >
                  Waitlist Manager
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
